import {ReactElement, useContext, useEffect, useState} from "react";
import {Button} from "@zendeskgarden/react-buttons";
import * as css from "./styles/selectionBar.module.scss";
import {CSVBoxButton} from "../../../components/csvbox/CSVBoxButton";
import {AuthContext} from "../../../contexts/AuthContext";
import {csvBoxExtractRows, csvBoxGeneralError} from "../../../global/csv_box_helpers";
import {
    useGenerateAcquisitionRentRollFileUploadUrlMutation,
    useProcessAcquisitionRentRollFileMutation
} from "../../../__generated__/generated_types";
import {toast} from "react-toastify";

interface AcquisitionRentRollDataItem {
    unitType: string;
    unit: string;
    squareFeet: string;
    scheduledRent: string;
    marketRent: string;
    leaseStart: string | null;
    leaseEnd: string | null;
}

function parseNumberField(raw: unknown): string | null  {
    if(typeof raw === "number") {
        return raw.toString();
    } else if(typeof raw === "string") {
        if(raw === "" || isNaN(parseFloat(raw))) {
            return null;
        }
        return raw;
    } else {
        return null;
    }
}

function extractData(values: unknown[]): AcquisitionRentRollDataItem[] {
    const retVals: AcquisitionRentRollDataItem[] = [];
    for(const value of values) {
        if(typeof value !== "object" || value === null) {
            continue;
        }

        const row = value as Record<string, unknown>;
        const virtualData = row["_virtual_data"] as Record<string, unknown> | undefined;

        if(typeof virtualData !== "object" || virtualData === null || virtualData === undefined) {
            continue;
        }

        const unitType = row["Unit Type"];
        const unit = row["Unit"];
        const squareFeetRaw = row["Square Feet"];
        const scheduledRentRaw = row["Scheduled Rent"];
        const marketRentRaw = row["Market Rent"];
        const leaseStart = virtualData["Lease Start Date"];
        const leaseEnd = virtualData["Lease End Date"];

        // Check string fields
        if(typeof unitType !== "string") {
            continue;
        }

        if(typeof unit !== "string") {
            continue;
        }

        if(typeof leaseStart !== "string" && leaseStart !== null && leaseStart !== undefined) {
            continue;
        }

        if(typeof leaseEnd !== "string" && leaseEnd !== null && leaseEnd !== undefined) {
            continue;
        }

        // Parse and validate number fields
        const squareFeet = parseNumberField(squareFeetRaw);
        if(squareFeet === null) {
            continue;
        }

        const scheduledRent = parseNumberField(scheduledRentRaw);
        if(scheduledRent === null) {
            continue;
        }

        const marketRent = parseNumberField(marketRentRaw);
        if(marketRent === null) {
            continue;
        }

        retVals.push({
            unitType: unitType,
            unit: unit,
            squareFeet: squareFeet,
            scheduledRent: scheduledRent,
            marketRent: marketRent,
            leaseStart: leaseStart === undefined || leaseStart === "" ? null : leaseStart,
            leaseEnd: leaseEnd === undefined || leaseEnd === "" ? null : leaseEnd
        });
    }

    return retVals;
}

export interface AcquisitionPropertyRentRollButtonProps {
    propertyId: string;
    jobCreated: (jobId: string, propertyId: string) => void;
}

export function AcquisitionPropertyRentRollButton(props: AcquisitionPropertyRentRollButtonProps): ReactElement {
    const {user} = useContext(AuthContext);

    const [generateUrl, {loading: loadingUrl, data: urlData}] = useGenerateAcquisitionRentRollFileUploadUrlMutation({
        notifyOnNetworkStatusChange: true
    });

    const [processRentRoll, {loading: loadingProcess}] = useProcessAcquisitionRentRollFileMutation({
        notifyOnNetworkStatusChange: true
    });

    const [data, setData] = useState<AcquisitionRentRollDataItem[] | null>(null);

    useEffect(() => {
        if(loadingUrl || !urlData) {
            return;
        }

        doUpload();
    }, [loadingUrl, urlData]);

    function doUpload(): void {
        if(!data) {
            return;
        }

        if(!urlData) {
            toast.error("Unable to determine file location.");
            return;
        }

        // Get URLs and keys
        const rentRollUrl = urlData.generateAcquisitionRentRollFileUploadUrl.signedUrl;
        const rentRollKey = urlData.generateAcquisitionRentRollFileUploadUrl.key;

        const encoder = new TextEncoder();
        const encoded = encoder.encode(JSON.stringify(data));

        // Create array of upload promises
        fetch(rentRollUrl, {
            method: 'PUT',
            body: encoded.buffer,
            headers: {
                'Content-Type': "application/json",
            }
        })
        .then(() => {
            return processRentRoll({
                variables: {
                    propertyId: props.propertyId,
                    fileKey: rentRollKey
                }
            });
        })
        .then(res => {
            if(res.data?.processAcquisitionRentRollFile) {
                props.jobCreated(res.data.processAcquisitionRentRollFile.id, props.propertyId);
                toast.success("Rent roll uploaded successfully.");
            }
        })
        .catch((error) => {
            console.error("Error uploading file:", error);
            toast.error("Error uploading file");
        });
    }

    function handleSave(data: unknown): void {
        const rows = csvBoxExtractRows(data);
        if(rows === undefined) {
            csvBoxGeneralError("rent roll");
            return;
        }

        const extracted = extractData(rows);
        if(extracted.length === 0) {
            csvBoxGeneralError("rent roll");
            return;
        }

        setData(extracted);
        generateUrl({
            variables: {
                propertyId: props.propertyId
            }
        });
    }

    return <CSVBoxButton
        licenseKey="PzwkB8EnuRrZTo46JXOu3XPYKhOuxk"
        user={{
            userId: user?.id ?? "unknown",
            name: user?.firstName || user?.lastName ? `${user?.firstName || ""} ${user?.lastName || ""}` : "unknown",
            email: user?.email ?? "unknown",
            clientId: user?.clientId ?? "unknown",
            clientName: user?.clientName ?? "unknown",
        }}
        onImport={(result, data) => {
            if(result) {
                handleSave(data);
            } else {
                csvBoxGeneralError("rent roll");
            }
        }}
        render={(launch, isLoading) => {
            return <Button className={css.userSelectionButton} onClick={launch} disabled={isLoading}>Update Rent Roll</Button>;
        }}
    />;
}